import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { videoPlayed } from '../../app/Search/actions/SearchActions';
import { selectLastPlayingVideoId } from '../../app/Search/selectors/searchSelectors';

// Global registry to track video refs by ID
const videoRefs = new Map<number, React.RefObject<HTMLVideoElement>>();

export const useMultiVideoPlayback = (
  videoId: number,
  videoRef: React.RefObject<HTMLVideoElement>
) => {
  const dispatch = useDispatch();
  const lastPlayingVideoId = useSelector(selectLastPlayingVideoId);
  const isPlaying = lastPlayingVideoId === videoId;
  const [playPromiseProcessing, setPlayPromiseProcessing] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);

  // Register this video ref globally
  useEffect(() => {
    videoRefs.set(videoId, videoRef);
    return () => {
      videoRefs.delete(videoId);
    };
  }, [videoId, videoRef]);

  const pause = useCallback(() => {
    if (videoRef.current && !playPromiseProcessing) {
      videoRef.current.pause();
    }
  }, [videoRef, playPromiseProcessing]);

  const play = useCallback(() => {
    if (videoRef.current && !hasPlayed) {
      // Pause the last playing video if it's different from this one
      if (lastPlayingVideoId && lastPlayingVideoId !== videoId) {
        const lastVideoRef = videoRefs.get(lastPlayingVideoId);
        if (lastVideoRef?.current) {
          lastVideoRef.current.pause();
        }
      }

      // Play this video with promise handling
      const playPromise = videoRef.current.play();
      setPlayPromiseProcessing(true);
      if (playPromise) {
        playPromise
          .then(() => {
            setPlayPromiseProcessing(false);
            setHasPlayed(true);
            dispatch(videoPlayed(videoId));
          })
          .catch((e) => {
            setPlayPromiseProcessing(false);
            // Only retry if it's not an AbortError (which is expected when interrupted)
            if (!(e instanceof DOMException && e.name === 'AbortError')) {
              // Reset hasPlayed to allow retry
              setHasPlayed(false);
              play();
            }
          });
      }
    }
  }, [videoRef, lastPlayingVideoId, videoId, dispatch, hasPlayed, pause]);

  const reset = useCallback(() => {
    setHasPlayed(false);
  }, []);

  return {
    isPlaying,
    play,
    pause,
    reset,
  };
};
